/**
 * SyllabusWorkspace.jsx
 * ─────────────────────
 * New Deskey-themed Syllabus experience:
 *  1. Home  → Classes grid
 *  2. Click class → Chapters grid (the same chapters teachers create in My Classes)
 *  3. Click chapter → list of existing Syllabi for that chapter + "Create Syllabus"
 *  4. Click syllabus (or create new) → SyllabusCreator — Deskey editor style with
 *     an AI chat rail (Gemini) that can ground itself in PDFs and textbook library,
 *     and a live, structured syllabus timeline on the right.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, BookOpen, ChevronRight, FileText, GraduationCap, Layers, Loader2,
  Plus, Send, Sparkles, Paperclip, Library, Trash2, X, ChevronDown, Target,
  CheckCircle2, ClipboardList, BookMarked, Wand2, Copy, Printer, Globe,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const token = localStorage.getItem('desky_token') || '';
  try {
    const res = await fetch(`${API}/api${path}`, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(opts.headers || {}),
      },
    });
    const t = await res.text();
    try { return JSON.parse(t); } catch { return { detail: t || `HTTP ${res.status}` }; }
  } catch (err) {
    return { detail: `Network error: ${err?.message || err}`, _network_error: true };
  }
};

const uploadPdf = async (syllabusId, file) => {
  const token = localStorage.getItem('desky_token') || '';
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API}/api/teacher/syllabus/${syllabusId}/pdfs`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  const t = await res.text();
  try { return JSON.parse(t); } catch { return { detail: t }; }
};

// ══════════════════════════════════════════════════════════════════════════
//  Entry point — routes between Home / ClassChapters / ChapterSyllabi / Creator
// ══════════════════════════════════════════════════════════════════════════
export default function SyllabusWorkspace() {
  // nav state: { view: 'home' | 'class' | 'chapter' | 'editor', classId, chapter, syllabusId }
  const [nav, setNav] = useState({ view: 'home' });

  if (nav.view === 'editor') {
    return (
      <SyllabusCreator
        syllabusId={nav.syllabusId}
        onBack={() => setNav({ view: 'chapter', classId: nav.classId, chapter: nav.chapter })}
      />
    );
  }
  if (nav.view === 'chapter') {
    return (
      <ChapterSyllabiView
        classId={nav.classId}
        chapter={nav.chapter}
        onBack={() => setNav({ view: 'class', classId: nav.classId })}
        onOpenSyllabus={(sid) => setNav({ ...nav, view: 'editor', syllabusId: sid })}
      />
    );
  }
  if (nav.view === 'class') {
    return (
      <ClassChaptersView
        classId={nav.classId}
        onBack={() => setNav({ view: 'home' })}
        onOpenChapter={(ch) => setNav({ view: 'chapter', classId: nav.classId, chapter: ch })}
      />
    );
  }
  return (
    <SyllabusHomeClasses onOpenClass={(cls) => setNav({ view: 'class', classId: cls.id, className: cls.name })} />
  );
}

// ── View 1: Classes grid ────────────────────────────────────────────────────
function SyllabusHomeClasses({ onOpenClass }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const r = await api('/teacher/classes/grouped');
      if (r.groups) setGroups(r.groups);
      setLoading(false);
    })();
  }, []);
  const classes = useMemo(() => {
    const out = [];
    (groups || []).forEach(g => (g.batches || []).forEach(b => (b.classes || []).forEach(c => out.push({ ...c, dept: g.department, batch: b.batch }))));
    return out;
  }, [groups]);
  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-8" data-testid="syllabus-home">
      <div className="mb-6">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#34A853] mb-1">Syllabus Workspace</p>
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Pick a class to build a syllabus</h1>
        <p className="text-[13px] text-gray-500 mt-1">Every class has its Chapters; each Chapter can hold one or more Syllabi you build with Desky AI.</p>
      </div>
      {loading ? (
        <p className="text-[13px] text-gray-400 text-center py-14">Loading your classes…</p>
      ) : classes.length === 0 ? (
        <div className="p-10 text-center bg-white rounded-2xl border border-dashed border-zinc-200">
          <GraduationCap className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          <p className="text-[13px] text-gray-500">No classes assigned yet. Ask your department admin to add you to a schedule.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map(c => (
            <button
              key={c.id}
              onClick={() => onOpenClass(c)}
              className="group rounded-2xl bg-white border border-zinc-200 hover:border-[#34A853]/50 hover:shadow-lg transition-all text-left overflow-hidden"
              data-testid={`syll-class-${c.id}`}
            >
              <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #34A853, #06B6D4, #8B5CF6)' }} />
              <div className="p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#34A853] mb-0.5 truncate">{(c.subjects || []).join(' · ') || 'Subject'}</p>
                <h4 className="text-[15px] font-extrabold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{c.name}</h4>
                <p className="text-[10px] text-gray-400 mt-0.5">{c.dept?.name} · {c.batch?.name}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[11px] text-gray-500">{c.student_count}/{c.capacity || '∞'} students</span>
                  <span className="text-[11px] font-semibold text-[#34A853] flex items-center gap-1 group-hover:gap-1.5 transition-all">Open <ChevronRight className="w-3.5 h-3.5" /></span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── View 2: Chapters for a class ────────────────────────────────────────────
function ClassChaptersView({ classId, onBack, onOpenChapter }) {
  const [chapters, setChapters] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const [d, l] = await Promise.all([
        api(`/teacher/classes/${classId}/detail`),
        api(`/teacher/classes/${classId}/lessons`),
      ]);
      if (d?.id) setDetail(d);
      if (l?.lessons) setChapters(l.lessons);
      setLoading(false);
    })();
  }, [classId]);
  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-8" data-testid="syll-class-view">
      <button onClick={onBack} className="text-[12px] font-semibold text-gray-600 hover:text-[#34A853] flex items-center gap-1 mb-4" data-testid="syll-back-home">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to classes
      </button>
      <div className="mb-6">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#34A853] mb-1">{(detail?.subjects || []).join(' · ') || 'Class'}</p>
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{detail?.name || 'Class'}</h1>
        <p className="text-[13px] text-gray-500 mt-1">Pick a chapter to see its syllabi, or build a new one inside it.</p>
      </div>
      {loading ? (
        <p className="text-[13px] text-gray-400 text-center py-14">Loading chapters…</p>
      ) : chapters.length === 0 ? (
        <div className="p-10 text-center bg-white rounded-2xl border border-dashed border-zinc-200">
          <BookOpen className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          <p className="text-[13px] text-gray-500">No chapters yet.</p>
          <p className="text-[12px] text-gray-400 mt-1">Create a chapter from <span className="font-semibold">My Classes → More details → Chapters</span>, then come back.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {chapters.map(ch => (
            <button
              key={ch.id}
              onClick={() => onOpenChapter(ch)}
              className="group rounded-2xl bg-white border border-zinc-200 hover:border-[#34A853]/50 hover:shadow-lg transition-all text-left overflow-hidden p-4"
              data-testid={`syll-chapter-${ch.id}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#34A853]/15 to-[#06B6D4]/15 border border-[#34A853]/20 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-[#0d9488]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-[#34A853]">Chapter</p>
                  <h4 className="text-[14px] font-extrabold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{ch.title}</h4>
                </div>
              </div>
              {ch.description && <p className="text-[11px] text-gray-600 line-clamp-2 leading-relaxed mb-3">{ch.description}</p>}
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-gray-500">{ch.topic_count || 0} topic{(ch.topic_count || 0) !== 1 ? 's' : ''}</span>
                <span className="text-[11px] font-semibold text-[#34A853] flex items-center gap-1 group-hover:gap-1.5 transition-all">Syllabi <ChevronRight className="w-3.5 h-3.5" /></span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── View 3: Syllabi inside a chapter ────────────────────────────────────────
function ChapterSyllabiView({ classId, chapter, onBack, onOpenSyllabus }) {
  const [syllabi, setSyllabi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const reload = useCallback(async () => {
    const r = await api(`/teacher/syllabus/by-chapter/${classId}/${chapter.id}`);
    if (r.syllabi) setSyllabi(r.syllabi);
    setLoading(false);
  }, [classId, chapter.id]);

  useEffect(() => { reload(); }, [reload]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete syllabus "${title}"? This cannot be undone.`)) return;
    const r = await api(`/teacher/syllabus/${id}`, { method: 'DELETE' });
    if (r.deleted) { toast.success('Syllabus deleted'); reload(); }
    else toast.error(r.detail || 'Failed');
  };

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-8" data-testid="syll-chapter-view">
      <button onClick={onBack} className="text-[12px] font-semibold text-gray-600 hover:text-[#34A853] flex items-center gap-1 mb-4" data-testid="syll-back-class">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to chapters
      </button>
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#34A853] mb-1">Chapter · Syllabi</p>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{chapter.title}</h1>
          {chapter.description && <p className="text-[13px] text-gray-500 mt-1 max-w-2xl">{chapter.description}</p>}
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 text-[13px] font-bold text-white bg-gradient-to-r from-[#34A853] to-[#0d9488] hover:shadow-md px-4 py-2.5 rounded-xl shadow-sm"
          data-testid="syll-create-btn"
        >
          <Sparkles className="w-4 h-4" /> Create syllabus
        </button>
      </div>
      {loading ? (
        <p className="text-[13px] text-gray-400 text-center py-14">Loading syllabi…</p>
      ) : syllabi.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-2xl border border-dashed border-zinc-200">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#34A853]/15 to-[#06B6D4]/15 border border-[#34A853]/20 flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-7 h-7 text-[#0d9488]" />
          </div>
          <p className="text-[14px] font-bold text-gray-800">No syllabus yet for this chapter</p>
          <p className="text-[12px] text-gray-500 mt-1 max-w-md mx-auto">Chat with Desky AI, attach a PDF or pick a textbook, and build out a weekly syllabus in minutes.</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-5 inline-flex items-center gap-2 text-[13px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] px-4 py-2 rounded-lg shadow-sm"
            data-testid="syll-create-empty-btn"
          >
            <Plus className="w-4 h-4" /> Start a syllabus
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {syllabi.map(s => (
            <SyllabusCard key={s.id} syllabus={s} onOpen={() => onOpenSyllabus(s.id)} onDelete={() => handleDelete(s.id, s.title)} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateSyllabusDialog
          classId={classId}
          chapter={chapter}
          onClose={() => setShowCreate(false)}
          onCreated={(sid) => { setShowCreate(false); onOpenSyllabus(sid); }}
        />
      )}
    </div>
  );
}

function SyllabusCard({ syllabus, onOpen, onDelete }) {
  const unitsCount = (syllabus.units || []).length;
  const updated = syllabus.updated_at ? new Date(syllabus.updated_at).toLocaleDateString() : '';
  return (
    <div className="group rounded-2xl bg-white border border-zinc-200 hover:border-[#34A853]/50 hover:shadow-md transition-all overflow-hidden" data-testid={`syll-card-${syllabus.id}`}>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#34A853]/12 to-[#06B6D4]/12 border border-[#34A853]/20 flex items-center justify-center flex-shrink-0">
            <Layers className="w-5 h-5 text-[#0d9488]" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-[14px] font-extrabold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{syllabus.title}</h4>
            <p className="text-[11px] text-gray-500 mt-0.5 truncate">{[syllabus.subject, syllabus.grade_level, syllabus.duration].filter(Boolean).join(' · ')}</p>
          </div>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50 transition-opacity" title="Delete syllabus" data-testid={`syll-delete-${syllabus.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-3 mt-3 text-[11px] text-gray-500">
          <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {(syllabus.objectives || []).length} objectives</span>
          <span className="flex items-center gap-1"><ClipboardList className="w-3 h-3" /> {unitsCount} unit{unitsCount !== 1 ? 's' : ''}</span>
          {updated && <span className="ml-auto text-gray-400">Updated {updated}</span>}
        </div>
        <button
          onClick={onOpen}
          className="mt-3 w-full text-[12px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] px-3 py-2 rounded-lg transition-colors"
          data-testid={`syll-open-${syllabus.id}`}
        >
          Open in Syllabus Creator
        </button>
      </div>
    </div>
  );
}

// ── Create-syllabus dialog ──────────────────────────────────────────────────
function CreateSyllabusDialog({ classId, chapter, onClose, onCreated }) {
  const [title, setTitle] = useState(`Syllabus — ${chapter.title}`);
  const [subject, setSubject] = useState('');
  const [grade, setGrade] = useState('');
  const [duration, setDuration] = useState('12 weeks');
  const [busy, setBusy] = useState(false);

  // Pre-fill subject + grade from the class detail
  useEffect(() => {
    (async () => {
      const d = await api(`/teacher/classes/${classId}/detail`);
      if (d?.id) {
        if ((d.subjects || []).length && !subject) setSubject((d.subjects || [])[0]);
        // Try to infer grade from class name (e.g. "Mathematics 11-A" → "Grade 11")
        const m = (d.name || '').match(/\b(\d{1,2})\b/);
        if (m && !grade) setGrade(`Grade ${m[1]}`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const submit = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    const r = await api('/teacher/syllabus/create-in-chapter', {
      method: 'POST',
      body: JSON.stringify({ class_id: classId, lesson_id: chapter.id, title: title.trim(), subject, grade_level: grade, duration }),
    });
    setBusy(false);
    if (r.syllabus) { toast.success('Syllabus created — chat with Desky AI to build it'); onCreated(r.syllabus.id); }
    else toast.error(r.detail || 'Failed');
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="syll-create-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-zinc-100 bg-gradient-to-br from-[#34A853]/8 via-white to-[#06B6D4]/6 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#34A853] mb-0.5">New syllabus</p>
            <h3 className="text-lg font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Set the basics — AI fills the rest</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <Field label="Title"><input value={title} onChange={e => setTitle(e.target.value)} className="inp" data-testid="syll-create-title" autoFocus /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Subject"><input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Physics" className="inp" data-testid="syll-create-subject" /></Field>
            <Field label="Grade / Level"><input value={grade} onChange={e => setGrade(e.target.value)} placeholder="e.g. Grade 11" className="inp" data-testid="syll-create-grade" /></Field>
          </div>
          <Field label="Duration"><input value={duration} onChange={e => setDuration(e.target.value)} placeholder="e.g. 12 weeks" className="inp" data-testid="syll-create-duration" /></Field>
          <p className="text-[11px] text-gray-500">You can attach PDFs / pick textbooks after creating — AI uses them as grounding.</p>
        </div>
        <div className="px-6 py-4 border-t border-zinc-100 bg-gray-50/60 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[12px] font-semibold text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100">Cancel</button>
          <button onClick={submit} disabled={busy || !title.trim()} className="text-[12px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] disabled:opacity-40 px-5 py-2 rounded-lg shadow-sm flex items-center gap-1.5" data-testid="syll-create-submit">
            {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating…</> : <><Sparkles className="w-3.5 h-3.5" /> Create</>}
          </button>
        </div>
      </div>
      <style>{`.inp{height:40px;width:100%;padding:0 12px;border-radius:8px;border:1px solid #e4e4e7;font-size:13px}.inp:focus{outline:none;border-color:#34A853;box-shadow:0 0 0 3px rgba(52,168,83,.15)}`}</style>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 block">{label}</span>
      {children}
    </label>
  );
}

// ══════════════════════════════════════════════════════════════════════════
//  View 4: Syllabus Creator — Deskey-themed editor + chat rail
// ══════════════════════════════════════════════════════════════════════════
function SyllabusCreator({ syllabusId, onBack }) {
  const [syllabus, setSyllabus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedPdfIds, setSelectedPdfIds] = useState([]);
  const [selectedTextbookIds, setSelectedTextbookIds] = useState([]);
  const [showTextbookPicker, setShowTextbookPicker] = useState(false);
  const [showStandardsPicker, setShowStandardsPicker] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [improvingIdx, setImprovingIdx] = useState(null);
  const fileInputRef = useRef(null);
  const chatScrollRef = useRef(null);

  const load = useCallback(async () => {
    const r = await api(`/teacher/syllabus/${syllabusId}`);
    if (r?.id) setSyllabus(r);
    setLoading(false);
  }, [syllabusId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [syllabus?.chat_history?.length]);

  const handleSend = async () => {
    const msg = message.trim();
    if (!msg || sending) return;
    setSending(true);
    setMessage('');
    const body = { message: msg, context_pdf_ids: selectedPdfIds, context_textbook_ids: selectedTextbookIds };
    let r = null;
    try {
      r = await api(`/teacher/syllabus/${syllabusId}/chat`, { method: 'POST', body: JSON.stringify(body) });
    } catch (_) { /* fall through */ }
    if (r?.syllabus) {
      setSyllabus(r.syllabus);
      if ((r.patch_keys || []).length) toast.success(`AI updated: ${r.patch_keys.join(', ')}`);
    } else {
      // Refetch so chat history + state stay in sync even on a timeout
      try {
        const fresh = await api(`/teacher/syllabus/${syllabusId}`);
        if (fresh?.id) setSyllabus(fresh);
      } catch (_) { /* ignore */ }
      toast.error(r?.detail || 'Chat failed — please retry');
    }
    setSending(false);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type?.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDFs are accepted');
      return;
    }
    toast.info('Uploading PDF…');
    const r = await uploadPdf(syllabusId, file);
    if (r.pdf) {
      toast.success(`Attached "${r.pdf.title}"`);
      setSyllabus(prev => ({ ...prev, attached_pdfs: [...(prev.attached_pdfs || []), { ...r.pdf, text_preview: '' }] }));
      setSelectedPdfIds(prev => [...prev, r.pdf.id]);
    } else {
      toast.error(r.detail || 'Upload failed');
    }
  };

  const handleRemovePdf = async (pdfId) => {
    const r = await api(`/teacher/syllabus/${syllabusId}/pdfs/${pdfId}`, { method: 'DELETE' });
    if (r.removed) {
      setSyllabus(prev => ({ ...prev, attached_pdfs: (prev.attached_pdfs || []).filter(p => p.id !== pdfId) }));
      setSelectedPdfIds(prev => prev.filter(id => id !== pdfId));
    }
  };

  const handleAddTextbook = async (tb) => {
    const r = await api(`/teacher/syllabus/${syllabusId}/textbooks`, {
      method: 'POST',
      body: JSON.stringify({ title: tb.title, author: tb.author, subject: tb.subject, grade_level: tb.grade_level, description: tb.description }),
    });
    if (r.textbook) {
      setSyllabus(prev => ({ ...prev, textbooks: [...(prev.textbooks || []), r.textbook] }));
      setSelectedTextbookIds(prev => [...prev, r.textbook.id]);
      toast.success(`Added "${r.textbook.title}"`);
    }
  };

  const handleRemoveTextbook = async (tbId) => {
    const r = await api(`/teacher/syllabus/${syllabusId}/textbooks/${tbId}`, { method: 'DELETE' });
    if (r.removed) {
      setSyllabus(prev => ({ ...prev, textbooks: (prev.textbooks || []).filter(t => t.id !== tbId) }));
      setSelectedTextbookIds(prev => prev.filter(id => id !== tbId));
    }
  };

  const patchSyllabus = (patch) => {
    setSyllabus(prev => ({ ...prev, ...patch }));
    api(`/teacher/syllabus/${syllabusId}`, { method: 'PUT', body: JSON.stringify(patch) });
  };

  const handleGenerateFull = async ({ frameworkId, extraInstructions }) => {
    setGenerating(true);
    setShowGenerate(false);
    toast.info('Generating world-standard syllabus — this usually takes 15–25 seconds…');
    let r = null;
    try {
      r = await api(`/teacher/syllabus/${syllabusId}/generate`, {
        method: 'POST',
        body: JSON.stringify({
          framework_id: frameworkId || '',
          extra_instructions: extraInstructions || '',
          context_pdf_ids: selectedPdfIds,
          context_textbook_ids: selectedTextbookIds,
        }),
      });
    } catch (_) { /* fall through to reload */ }

    // Always reload the syllabus — covers cases where the proxy closed the long-running POST
    // but the backend still persisted the generation.
    try {
      const fresh = await api(`/teacher/syllabus/${syllabusId}`);
      if (fresh?.id) setSyllabus(fresh);
    } catch (_) { /* ignore */ }

    setGenerating(false);
    const keys = r?.patch_keys || [];
    if (keys.length) {
      toast.success(`Generated: ${keys.join(', ')}`);
    } else if (r?._network_error) {
      toast.success('Generation finished — refreshed the syllabus from server');
    } else if (r?.detail) {
      toast.error(r.detail);
    } else {
      toast.info('Refreshed — AI output is visible in the editor');
    }
  };

  const handleImproveUnit = async (unitIndex, instruction) => {
    setImprovingIdx(unitIndex);
    toast.info('Improving unit with AI…');
    let r = null;
    try {
      r = await api(`/teacher/syllabus/${syllabusId}/units/${unitIndex}/improve`, {
        method: 'POST',
        body: JSON.stringify({ instruction: instruction || '' }),
      });
    } catch (_) { /* fall through */ }
    setImprovingIdx(null);
    if (r?.unit) {
      const next = [...(syllabus.units || [])];
      next[unitIndex] = r.unit;
      setSyllabus(prev => ({ ...prev, units: next }));
      toast.success(`Unit ${r.unit.unit_number || unitIndex + 1} improved`);
    } else {
      // Fallback: refetch so the user sees the current state
      try {
        const fresh = await api(`/teacher/syllabus/${syllabusId}`);
        if (fresh?.id) setSyllabus(fresh);
      } catch (_) { /* ignore */ }
      toast.error(r?.detail || 'Improve failed — please retry');
    }
  };

  const handleDuplicateUnit = (unitIndex) => {
    const units = syllabus.units || [];
    const src = units[unitIndex];
    if (!src) return;
    const copy = JSON.parse(JSON.stringify(src));
    copy.id = `unit_${Date.now()}`;
    copy.unit_number = units.length + 1;
    copy.title = `${src.title || 'Unit'} (copy)`;
    // Rewrite topic ids to avoid collisions
    (copy.topics || []).forEach((t, j) => { t.id = `topic_${Date.now()}_${j}`; });
    const next = [...units, copy];
    patchSyllabus({ units: next });
    toast.success('Unit duplicated');
  };

  const handlePrint = () => {
    // Encode syllabus snapshot and open a printable view in a new tab
    try {
      const payload = encodeURIComponent(JSON.stringify(syllabus));
      const w = window.open('', '_blank', 'noopener,noreferrer');
      if (!w) { toast.error('Popup blocked — allow popups and retry'); return; }
      w.document.open();
      w.document.write(renderPrintableHtml(syllabus));
      w.document.close();
    } catch (e) {
      toast.error('Could not open printable view');
      // eslint-disable-next-line no-unused-expressions
      void e;
    }
  };

  if (loading || !syllabus) {
    return (
      <div className="h-full flex items-center justify-center bg-white" data-testid="syll-creator-loading">
        <div className="flex flex-col items-center">
          <Loader2 className="w-6 h-6 text-[#34A853] animate-spin mb-2" />
          <p className="text-[12px] text-gray-500">Loading syllabus…</p>
        </div>
      </div>
    );
  }

  const attachedPdfs = syllabus.attached_pdfs || [];
  const textbooks = syllabus.textbooks || [];
  const chatHistory = syllabus.chat_history || [];

  return (
    <div className="h-full flex bg-[#f6f7f9]" data-testid="syll-creator">
      {/* Chat rail */}
      <aside className="w-[400px] flex-shrink-0 border-r border-zinc-200 bg-white flex flex-col">
        <div className="px-4 py-3 border-b border-zinc-100 bg-gradient-to-br from-[#34A853]/6 via-white to-[#06B6D4]/4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#34A853] to-[#0d9488] flex items-center justify-center shadow-sm">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#34A853]">Desky Syllabus AI</p>
              <p className="text-[12px] font-semibold text-gray-900 truncate">Chat to build your syllabus</p>
            </div>
          </div>
        </div>

        {/* Attached context chips */}
        <div className="px-4 py-2 border-b border-zinc-100 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Context ({selectedPdfIds.length + selectedTextbookIds.length} active)</p>
            <div className="flex items-center gap-1">
              <button onClick={() => fileInputRef.current?.click()} className="text-[10px] font-bold text-[#0d9488] hover:bg-[#34A853]/10 px-2 py-1 rounded-md flex items-center gap-1" data-testid="syll-attach-pdf"><Paperclip className="w-3 h-3" /> PDF</button>
              <button onClick={() => setShowTextbookPicker(true)} className="text-[10px] font-bold text-[#0d9488] hover:bg-[#34A853]/10 px-2 py-1 rounded-md flex items-center gap-1" data-testid="syll-pick-textbook"><Library className="w-3 h-3" /> Textbook</button>
            </div>
            <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" onChange={handlePdfUpload} className="hidden" data-testid="syll-pdf-input" />
          </div>
          {(attachedPdfs.length === 0 && textbooks.length === 0) ? (
            <p className="text-[11px] text-gray-400 italic">No grounding yet — attach a PDF or pick a textbook so the AI stays accurate.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {attachedPdfs.map(pdf => {
                const active = selectedPdfIds.includes(pdf.id);
                return (
                  <button key={pdf.id} onClick={() => setSelectedPdfIds(prev => active ? prev.filter(x => x !== pdf.id) : [...prev, pdf.id])} className={`text-[10px] font-semibold px-2 py-1 rounded-md border flex items-center gap-1 transition-colors ${active ? 'bg-[#34A853]/10 border-[#34A853]/40 text-[#0d9488]' : 'bg-white border-zinc-200 text-gray-600 hover:border-zinc-300'}`} title={pdf.title} data-testid={`syll-pdf-chip-${pdf.id}`}>
                    <FileText className="w-2.5 h-2.5" /> {pdf.title.slice(0, 22)}
                    <span onClick={(e) => { e.stopPropagation(); handleRemovePdf(pdf.id); }} className="ml-1 text-gray-400 hover:text-red-500"><X className="w-2.5 h-2.5" /></span>
                  </button>
                );
              })}
              {textbooks.map(tb => {
                const active = selectedTextbookIds.includes(tb.id);
                return (
                  <button key={tb.id} onClick={() => setSelectedTextbookIds(prev => active ? prev.filter(x => x !== tb.id) : [...prev, tb.id])} className={`text-[10px] font-semibold px-2 py-1 rounded-md border flex items-center gap-1 transition-colors ${active ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-white border-zinc-200 text-gray-600 hover:border-zinc-300'}`} title={tb.title} data-testid={`syll-tb-chip-${tb.id}`}>
                    <BookMarked className="w-2.5 h-2.5" /> {tb.title.slice(0, 22)}
                    <span onClick={(e) => { e.stopPropagation(); handleRemoveTextbook(tb.id); }} className="ml-1 text-gray-400 hover:text-red-500"><X className="w-2.5 h-2.5" /></span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Chat history */}
        <div ref={chatScrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="syll-chat-history">
          {chatHistory.map(m => <ChatBubble key={m.id} msg={m} />)}
          {sending && (
            <div className="flex gap-2 items-start">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#34A853] to-[#0d9488] flex items-center justify-center flex-shrink-0"><Sparkles className="w-3 h-3 text-white" /></div>
              <div className="bg-white border border-zinc-200 rounded-xl px-3 py-2 text-[12px] text-gray-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Thinking…</div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="px-4 py-3 border-t border-zinc-100 bg-white">
          <div className="rounded-xl border border-zinc-200 focus-within:border-[#34A853]/40 focus-within:ring-2 focus-within:ring-[#34A853]/20 bg-white transition-all">
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Ask Desky AI — “Create a 12-week syllabus grounded in the attached PDF”"
              className="w-full resize-none bg-transparent outline-none text-[13px] px-3 py-2.5 min-h-[64px] max-h-[160px]"
              data-testid="syll-chat-input"
            />
            <div className="flex items-center justify-between px-2 py-1.5 border-t border-zinc-100">
              <p className="text-[10px] text-gray-400">Enter to send · Shift+Enter for newline</p>
              <button onClick={handleSend} disabled={!message.trim() || sending} className="text-[11px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] disabled:opacity-40 px-3 py-1.5 rounded-md flex items-center gap-1" data-testid="syll-chat-send">
                <Send className="w-3 h-3" /> Send
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main pane — live syllabus */}
      <div className="flex-1 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-3 border-b border-zinc-200 bg-white/80 backdrop-blur flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800" data-testid="syll-back"><ArrowLeft className="w-4 h-4" /></button>
          <div className="flex-1 min-w-0">
            <input
              value={syllabus.title || ''}
              onChange={e => setSyllabus(prev => ({ ...prev, title: e.target.value }))}
              onBlur={() => patchSyllabus({ title: syllabus.title })}
              className="text-[16px] font-extrabold text-gray-900 bg-transparent outline-none w-full tracking-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="syll-title"
            />
            <p className="text-[11px] text-gray-500 truncate">{[syllabus.subject, syllabus.grade_level, syllabus.duration].filter(Boolean).join(' · ') || 'Chat with AI to define subject, grade, duration'}</p>
          </div>
          <button
            onClick={() => setShowGenerate(true)}
            disabled={generating}
            className="flex items-center gap-1.5 text-[12px] font-bold text-white bg-gradient-to-r from-[#34A853] to-[#0d9488] hover:shadow-md disabled:opacity-50 px-3 py-1.5 rounded-lg shadow-sm"
            title="Generate full syllabus with AI"
            data-testid="syll-generate-btn"
          >
            {generating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating</> : <><Wand2 className="w-3.5 h-3.5" /> Generate</>}
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 text-[12px] font-semibold text-gray-700 hover:text-[#0d9488] hover:bg-[#34A853]/5 border border-zinc-200 hover:border-[#34A853]/40 px-3 py-1.5 rounded-lg"
            title="Open printable view in new tab"
            data-testid="syll-print-btn"
          >
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded border border-emerald-200">Auto-saved</span>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-6 space-y-5">
          {/* Meta edit panel */}
          <MetaPanel syllabus={syllabus} onPatch={patchSyllabus} />

          {/* Objectives */}
          <ObjectivesPanel objectives={syllabus.objectives || []} onPatch={(objectives) => patchSyllabus({ objectives })} />

          {/* Standards */}
          <StandardsPanel standards={syllabus.standards || ''} onPatch={(standards) => patchSyllabus({ standards })} onPickFramework={() => setShowStandardsPicker(true)} />

          {/* Units */}
          <UnitsPanel
            units={syllabus.units || []}
            onPatch={(units) => patchSyllabus({ units })}
            onImprove={handleImproveUnit}
            onDuplicate={handleDuplicateUnit}
            improvingIdx={improvingIdx}
          />

          {/* Weekly pacing */}
          <WeeklyPacingPanel syllabus={syllabus} />

          {/* Resources summary */}
          <ResourcesPanel syllabus={syllabus} />
        </div>
      </div>

      {showTextbookPicker && (
        <TextbookPicker
          onClose={() => setShowTextbookPicker(false)}
          onPick={(tb) => { handleAddTextbook(tb); setShowTextbookPicker(false); }}
          existingTitles={new Set(textbooks.map(t => t.title.toLowerCase()))}
        />
      )}

      {showStandardsPicker && (
        <StandardsPicker
          currentStandards={syllabus.standards || ''}
          onClose={() => setShowStandardsPicker(false)}
          onPick={(fw) => {
            const line = `${fw.label} (${fw.grades})`;
            const existing = (syllabus.standards || '').trim();
            const next = existing ? `${existing}\n${line}` : line;
            patchSyllabus({ standards: next });
            setShowStandardsPicker(false);
            toast.success(`Aligned to ${fw.label}`);
          }}
        />
      )}

      {showGenerate && (
        <GenerateDialog
          onClose={() => setShowGenerate(false)}
          onSubmit={handleGenerateFull}
          hasContext={attachedPdfs.length + textbooks.length > 0}
        />
      )}
    </div>
  );
}

function ChatBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-2 items-start ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${isUser ? 'bg-zinc-200 text-zinc-700' : 'bg-gradient-to-br from-[#34A853] to-[#0d9488] text-white'}`}>
        {isUser ? <span className="text-[10px] font-bold">You</span> : <Sparkles className="w-3 h-3" />}
      </div>
      <div className={`max-w-[85%] rounded-xl px-3 py-2 text-[12px] leading-relaxed ${isUser ? 'bg-[#34A853] text-white' : 'bg-white border border-zinc-200 text-gray-800'}`}>
        <div className="whitespace-pre-wrap break-words">{msg.content}</div>
        {msg.patch_applied && (msg.patch_keys || []).length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {(msg.patch_keys || []).map(k => (
              <span key={k} className="text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200">{k} updated</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MetaPanel({ syllabus, onPatch }) {
  const [subject, setSubject] = useState(syllabus.subject || '');
  const [grade, setGrade] = useState(syllabus.grade_level || '');
  const [duration, setDuration] = useState(syllabus.duration || '');
  useEffect(() => { setSubject(syllabus.subject || ''); setGrade(syllabus.grade_level || ''); setDuration(syllabus.duration || ''); }, [syllabus.id, syllabus.subject, syllabus.grade_level, syllabus.duration]);
  return (
    <section className="rounded-2xl bg-white border border-zinc-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className="w-4 h-4 text-[#0d9488]" />
        <h3 className="text-[13px] font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Course meta</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Subject"><input value={subject} onChange={e => setSubject(e.target.value)} onBlur={() => onPatch({ subject })} className="inp2" data-testid="syll-meta-subject" /></Field>
        <Field label="Grade / Level"><input value={grade} onChange={e => setGrade(e.target.value)} onBlur={() => onPatch({ grade_level: grade })} className="inp2" data-testid="syll-meta-grade" /></Field>
        <Field label="Duration"><input value={duration} onChange={e => setDuration(e.target.value)} onBlur={() => onPatch({ duration })} className="inp2" data-testid="syll-meta-duration" /></Field>
      </div>
      <style>{`.inp2{height:36px;width:100%;padding:0 10px;border-radius:6px;border:1px solid #e4e4e7;font-size:12px;background:white}.inp2:focus{outline:none;border-color:#34A853;box-shadow:0 0 0 3px rgba(52,168,83,.15)}`}</style>
    </section>
  );
}

function ObjectivesPanel({ objectives, onPatch }) {
  const [items, setItems] = useState(objectives);
  const [adding, setAdding] = useState('');
  useEffect(() => { setItems(objectives); }, [objectives]);
  const add = () => {
    const t = adding.trim(); if (!t) return;
    const next = [...items, t]; setItems(next); setAdding(''); onPatch(next);
  };
  const remove = (i) => { const next = items.filter((_, idx) => idx !== i); setItems(next); onPatch(next); };
  return (
    <section className="rounded-2xl bg-white border border-zinc-200 p-5" data-testid="syll-objectives">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-[#0d9488]" />
        <h3 className="text-[13px] font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Learning objectives</h3>
        <span className="text-[10px] text-gray-400 ml-auto">{items.length} item{items.length !== 1 ? 's' : ''}</span>
      </div>
      {items.length === 0 ? <p className="text-[11px] text-gray-400 italic mb-3">Tell AI: "Add 5 learning objectives aligned to the attached PDF"</p> : null}
      <ul className="space-y-1.5 mb-2">
        {items.map((o, i) => (
          <li key={i} className="flex items-start gap-2 group">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
            <span className="flex-1 text-[12px] text-gray-800 leading-relaxed">{o}</span>
            <button onClick={() => remove(i)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity"><X className="w-3 h-3" /></button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <input value={adding} onChange={e => setAdding(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} placeholder="Add an objective…" className="flex-1 h-9 px-3 text-[12px] rounded-lg border border-zinc-200 focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20" data-testid="syll-objective-add" />
        <button onClick={add} disabled={!adding.trim()} className="text-[11px] font-bold text-[#0d9488] hover:bg-[#34A853]/10 border border-[#34A853]/30 px-3 rounded-lg disabled:opacity-40" data-testid="syll-objective-add-btn">Add</button>
      </div>
    </section>
  );
}

function StandardsPanel({ standards, onPatch, onPickFramework }) {
  const [val, setVal] = useState(standards || '');
  useEffect(() => { setVal(standards || ''); }, [standards]);
  return (
    <section className="rounded-2xl bg-white border border-zinc-200 p-5" data-testid="syll-standards">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="w-4 h-4 text-[#0d9488]" />
        <h3 className="text-[13px] font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Standards / Alignment</h3>
        <button
          onClick={onPickFramework}
          className="ml-auto text-[11px] font-bold text-[#0d9488] hover:bg-[#34A853]/10 border border-[#34A853]/30 px-2.5 py-1 rounded-md flex items-center gap-1"
          data-testid="syll-pick-framework"
        >
          <Globe className="w-3 h-3" /> Pick framework
        </button>
      </div>
      <textarea
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => onPatch(val)}
        rows={3}
        placeholder="Pick a framework above (CBSE / IB DP / Cambridge / NGSS / Common Core / AP) or paste custom codes — e.g. 'CBSE Class XI Physics Unit 4: Oscillations; NGSS HS-PS4-1'"
        className="w-full text-[12px] rounded-lg border border-zinc-200 focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20 p-3 resize-none"
      />
    </section>
  );
}

function UnitsPanel({ units, onPatch, onImprove, onDuplicate, improvingIdx }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));
  const addUnit = () => {
    const next = [...units, {
      id: `u_${Date.now()}`,
      unit_number: units.length + 1,
      title: `Unit ${units.length + 1}`,
      description: '',
      duration: '',
      prerequisites: [],
      differentiation: '',
      topics: [],
      assessment: { type: 'test', title: '', description: '', weight: 10, rubric_criteria: [] },
    }];
    onPatch(next);
  };
  const patchUnit = (idx, patch) => { const next = units.map((u, i) => i === idx ? { ...u, ...patch } : u); onPatch(next); };
  const removeUnit = (idx) => { if (!window.confirm('Remove this unit?')) return; onPatch(units.filter((_, i) => i !== idx)); };
  const move = (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= units.length) return;
    const next = [...units]; [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((u, i) => { u.unit_number = i + 1; });
    onPatch(next);
  };
  return (
    <section className="rounded-2xl bg-white border border-zinc-200 p-5" data-testid="syll-units">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="w-4 h-4 text-[#0d9488]" />
        <h3 className="text-[13px] font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Units</h3>
        <span className="text-[10px] text-gray-400">{units.length} unit{units.length !== 1 ? 's' : ''}</span>
        <button onClick={addUnit} className="ml-auto text-[11px] font-bold text-[#0d9488] hover:bg-[#34A853]/10 border border-[#34A853]/30 px-2.5 py-1 rounded-md flex items-center gap-1" data-testid="syll-unit-add"><Plus className="w-3 h-3" /> Unit</button>
      </div>
      {units.length === 0 ? (
        <p className="text-[12px] text-gray-400 italic">No units yet. Click <span className="font-semibold text-[#34A853]">Generate</span> above for a one-shot syllabus, or ask AI: "Build out 5 units for a 12-week course".</p>
      ) : (
        <div className="space-y-2">
          {units.map((u, i) => (
            <UnitRow key={u.id || i}
              unit={u} index={i} total={units.length}
              expanded={!!expanded[u.id || i]}
              improving={improvingIdx === i}
              onToggle={() => toggle(u.id || i)}
              onPatch={(patch) => patchUnit(i, patch)}
              onRemove={() => removeUnit(i)}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, 1)}
              onImprove={(instruction) => onImprove(i, instruction)}
              onDuplicate={() => onDuplicate(i)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function UnitRow({ unit, index, total, expanded, improving, onToggle, onPatch, onRemove, onMoveUp, onMoveDown, onImprove, onDuplicate }) {
  const topicCount = (unit.topics || []).length;
  const [improveInput, setImproveInput] = useState('');
  const [showImprove, setShowImprove] = useState(false);
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid={`syll-unit-${index}`}>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-gradient-to-r from-[#34A853]/5 to-transparent cursor-pointer hover:from-[#34A853]/10" onClick={onToggle}>
        <button className="text-gray-400 hover:text-gray-700"><ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? '' : '-rotate-90'}`} /></button>
        <span className="text-[10px] font-bold uppercase tracking-wider bg-white border border-zinc-200 text-gray-600 px-1.5 py-0.5 rounded">Unit {unit.unit_number || index + 1}</span>
        <input
          value={unit.title || ''}
          onChange={e => onPatch({ title: e.target.value })}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 bg-transparent text-[13px] font-bold text-gray-900 outline-none min-w-0"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        />
        <span className="text-[10px] text-gray-400">{topicCount} topic{topicCount !== 1 ? 's' : ''}</span>
        <button onClick={(e) => { e.stopPropagation(); setShowImprove(s => !s); }} title="Improve with AI" className="text-gray-500 hover:text-[#0d9488] p-1 rounded disabled:opacity-40" disabled={improving} data-testid={`syll-unit-improve-${index}`}>
          {improving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
        </button>
        <button onClick={(e) => { e.stopPropagation(); onDuplicate(); }} title="Duplicate unit" className="text-gray-500 hover:text-gray-800 p-1 rounded" data-testid={`syll-unit-duplicate-${index}`}><Copy className="w-3 h-3" /></button>
        <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={index === 0} className="text-[10px] font-bold text-gray-500 hover:text-gray-900 disabled:opacity-30 px-1">↑</button>
        <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={index === total - 1} className="text-[10px] font-bold text-gray-500 hover:text-gray-900 disabled:opacity-30 px-1">↓</button>
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-gray-400 hover:text-red-500 p-1 rounded"><Trash2 className="w-3 h-3" /></button>
      </div>
      {showImprove && (
        <div className="px-3 py-2 border-t border-zinc-100 bg-[#34A853]/5 flex items-center gap-2">
          <input
            value={improveInput}
            onChange={e => setImproveInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { onImprove(improveInput.trim()); setShowImprove(false); setImproveInput(''); } }}
            placeholder="Tell AI how — e.g. 'Add differentiation for advanced learners, include lab'"
            className="flex-1 h-8 px-2.5 text-[11px] rounded-md border border-zinc-200 focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
            data-testid={`syll-unit-improve-input-${index}`}
            autoFocus
          />
          <button onClick={() => { onImprove(improveInput.trim()); setShowImprove(false); setImproveInput(''); }} className="text-[10px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] px-3 py-1.5 rounded-md" data-testid={`syll-unit-improve-submit-${index}`}>Improve</button>
          <button onClick={() => { setShowImprove(false); setImproveInput(''); }} className="text-[10px] font-semibold text-gray-500 hover:text-gray-800">Cancel</button>
        </div>
      )}
      {expanded && (
        <div className="p-3 space-y-3 border-t border-zinc-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input value={unit.duration || ''} onChange={e => onPatch({ duration: e.target.value })} placeholder="Duration (e.g. 2 weeks / 6 lessons)" className="inp2" />
            <input value={unit.description || ''} onChange={e => onPatch({ description: e.target.value })} placeholder="Short description" className="inp2 md:col-span-2" />
          </div>
          {/* Prerequisites */}
          <ChipListField
            label="Prerequisites"
            items={unit.prerequisites || []}
            placeholder="e.g. Basic algebra"
            onChange={(prerequisites) => onPatch({ prerequisites })}
            testid={`syll-unit-prereq-${index}`}
          />
          {/* Differentiation */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Differentiation</p>
            <textarea
              value={unit.differentiation || ''}
              onChange={e => onPatch({ differentiation: e.target.value })}
              rows={2}
              placeholder="How will you scaffold for below-grade learners and stretch advanced ones?"
              className="w-full text-[11px] rounded-md border border-zinc-200 p-2 resize-none focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
              data-testid={`syll-unit-diff-${index}`}
            />
          </div>
          {/* Topics */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Topics</p>
              <button onClick={() => onPatch({ topics: [...(unit.topics || []), { id: `t_${Date.now()}`, title: 'New topic', subtopics: [], learning_objectives: [], duration: '', teaching_methods: [], resources: [] }] })} className="text-[10px] font-bold text-[#0d9488] hover:bg-[#34A853]/10 px-2 py-0.5 rounded">+ Topic</button>
            </div>
            <div className="space-y-1.5">
              {(unit.topics || []).length === 0 && <p className="text-[11px] text-gray-400 italic">No topics yet.</p>}
              {(unit.topics || []).map((t, ti) => (
                <TopicRow
                  key={t.id || ti}
                  topic={t}
                  onPatch={(patch) => onPatch({ topics: (unit.topics || []).map((x, j) => j === ti ? { ...x, ...patch } : x) })}
                  onRemove={() => onPatch({ topics: (unit.topics || []).filter((_, j) => j !== ti) })}
                />
              ))}
            </div>
          </div>
          {/* Assessment */}
          <div className="rounded-lg bg-gray-50/80 border border-zinc-100 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1.5">Assessment</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <select value={(unit.assessment || {}).type || 'test'} onChange={e => onPatch({ assessment: { ...(unit.assessment || {}), type: e.target.value } })} className="inp2">
                <option value="test">Test</option><option value="project">Project</option><option value="presentation">Presentation</option><option value="essay">Essay</option><option value="quiz">Quiz</option><option value="portfolio">Portfolio</option><option value="lab">Lab</option><option value="debate">Debate</option><option value="rubric">Rubric-based</option>
              </select>
              <input value={(unit.assessment || {}).title || ''} onChange={e => onPatch({ assessment: { ...(unit.assessment || {}), title: e.target.value } })} placeholder="Assessment title" className="inp2 md:col-span-2" />
              <input
                type="number" min="0" max="100"
                value={(unit.assessment || {}).weight ?? ''}
                onChange={e => onPatch({ assessment: { ...(unit.assessment || {}), weight: Number(e.target.value) || 0 } })}
                placeholder="Weight %" className="inp2"
              />
            </div>
            <textarea value={(unit.assessment || {}).description || ''} onChange={e => onPatch({ assessment: { ...(unit.assessment || {}), description: e.target.value } })} rows={2} placeholder="What's being assessed?" className="mt-2 w-full text-[11px] rounded-md border border-zinc-200 p-2 resize-none focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20" />
            <div className="mt-2">
              <ChipListField
                label="Rubric criteria"
                items={(unit.assessment || {}).rubric_criteria || []}
                placeholder="e.g. Accuracy, Clarity, Evidence"
                onChange={(rubric_criteria) => onPatch({ assessment: { ...(unit.assessment || {}), rubric_criteria } })}
                testid={`syll-unit-rubric-${index}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChipListField({ label, items, placeholder, onChange, testid }) {
  const [val, setVal] = useState('');
  const add = () => { const t = val.trim(); if (!t) return; onChange([...items, t]); setVal(''); };
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">{label}</p>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {items.map((x, i) => (
          <span key={i} className="text-[10px] bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-full flex items-center gap-1 group">
            {x}
            <button onClick={() => remove(i)} className="text-gray-400 group-hover:text-red-500"><X className="w-2.5 h-2.5" /></button>
          </span>
        ))}
        {items.length === 0 && <span className="text-[10px] text-gray-400 italic">None yet</span>}
      </div>
      <div className="flex gap-1">
        <input
          value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          placeholder={placeholder}
          className="flex-1 h-7 px-2 text-[11px] rounded-md border border-zinc-200 focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
          data-testid={testid}
        />
        <button onClick={add} disabled={!val.trim()} className="text-[10px] font-bold text-[#0d9488] hover:bg-[#34A853]/10 border border-[#34A853]/30 px-2 rounded-md disabled:opacity-40">Add</button>
      </div>
    </div>
  );
}

function WeeklyPacingPanel({ syllabus }) {
  const units = syllabus.units || [];
  if (units.length === 0) return null;
  // Parse total weeks from duration string
  const totalMatch = (syllabus.duration || '').match(/(\d+)/);
  const totalWeeks = totalMatch ? parseInt(totalMatch[1], 10) : units.reduce((acc, u) => {
    const m = (u.duration || '').match(/(\d+)/); return acc + (m ? parseInt(m[1], 10) : 1);
  }, 0);
  let w = 1;
  const rows = units.map(u => {
    const m = (u.duration || '').match(/(\d+)/);
    const len = m ? parseInt(m[1], 10) : Math.max(1, Math.round((totalWeeks || units.length) / units.length));
    const from = w; const to = Math.min(totalWeeks || from + len - 1, from + len - 1);
    w = to + 1;
    return { ...u, from, to };
  });
  return (
    <section className="rounded-2xl bg-white border border-zinc-200 p-5" data-testid="syll-pacing">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className="w-4 h-4 text-[#0d9488]" />
        <h3 className="text-[13px] font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Weekly pacing</h3>
        <span className="text-[10px] text-gray-400">{totalWeeks ? `${totalWeeks} weeks` : `${units.length} units`}</span>
      </div>
      <div className="space-y-1.5">
        {rows.map((u, i) => (
          <div key={u.id || i} className="flex items-center gap-3 rounded-lg bg-gray-50/70 border border-zinc-100 p-2.5" data-testid={`syll-pacing-${i}`}>
            <span className="text-[10px] font-bold uppercase tracking-wider bg-[#34A853]/10 text-[#0d9488] border border-[#34A853]/20 px-2 py-0.5 rounded min-w-[70px] text-center">Week {u.from}{u.to !== u.from ? `–${u.to}` : ''}</span>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-bold text-gray-800 truncate">{u.title || `Unit ${u.unit_number || i + 1}`}</p>
              {u.description && <p className="text-[11px] text-gray-500 truncate">{u.description}</p>}
            </div>
            <span className="text-[10px] text-gray-400">{(u.topics || []).length} topics</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function TopicRow({ topic, onPatch, onRemove }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-2 group">
      <div className="flex items-center gap-2">
        <div className="w-1 h-6 rounded-full bg-[#34A853]/30" />
        <input value={topic.title || ''} onChange={e => onPatch({ title: e.target.value })} className="flex-1 bg-transparent text-[12px] font-semibold text-gray-800 outline-none" />
        <input value={topic.duration || ''} onChange={e => onPatch({ duration: e.target.value })} placeholder="e.g. 2 classes" className="w-28 text-[11px] text-gray-600 bg-transparent outline-none border border-transparent hover:border-zinc-200 rounded px-2 py-0.5" />
        <button onClick={onRemove} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity"><Trash2 className="w-3 h-3" /></button>
      </div>
      {(topic.learning_objectives || []).length > 0 && (
        <div className="mt-1 pl-3 text-[11px] text-gray-600 leading-relaxed">
          {(topic.learning_objectives || []).slice(0, 3).map((o, i) => <div key={i} className="flex gap-1"><span className="text-emerald-500">·</span>{o}</div>)}
        </div>
      )}
      {(topic.subtopics || []).length > 0 && (
        <div className="mt-1 pl-3 flex flex-wrap gap-1">
          {(topic.subtopics || []).map((st, i) => <span key={i} className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">{st}</span>)}
        </div>
      )}
      {(topic.resources || []).length > 0 && (
        <p className="mt-1 pl-3 text-[10px] text-gray-500">📚 {(topic.resources || []).join(' · ')}</p>
      )}
    </div>
  );
}

function ResourcesPanel({ syllabus }) {
  const pdfs = syllabus.attached_pdfs || [];
  const tbs = syllabus.textbooks || [];
  if (pdfs.length === 0 && tbs.length === 0) return null;
  return (
    <section className="rounded-2xl bg-white border border-zinc-200 p-5" data-testid="syll-resources">
      <div className="flex items-center gap-2 mb-3">
        <Library className="w-4 h-4 text-[#0d9488]" />
        <h3 className="text-[13px] font-extrabold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Grounding material</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {pdfs.map(p => (
          <div key={p.id} className="rounded-lg border border-zinc-200 bg-white p-2.5 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#0d9488]" />
            <div className="flex-1 min-w-0"><p className="text-[12px] font-semibold text-gray-800 truncate">{p.title}</p><p className="text-[10px] text-gray-400">PDF</p></div>
          </div>
        ))}
        {tbs.map(t => (
          <div key={t.id} className="rounded-lg border border-zinc-200 bg-white p-2.5 flex items-center gap-2">
            <BookMarked className="w-4 h-4 text-indigo-600" />
            <div className="flex-1 min-w-0"><p className="text-[12px] font-semibold text-gray-800 truncate">{t.title}</p><p className="text-[10px] text-gray-400 truncate">{t.author || 'Textbook'}</p></div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Textbook picker modal ───────────────────────────────────────────────────
function TextbookPicker({ onClose, onPick, existingTitles }) {
  const [lib, setLib] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', author: '', subject: '', grade_level: '', description: '' });
  useEffect(() => {
    (async () => {
      const r = await api('/teacher/syllabus/library/textbooks');
      if (r.textbooks) setLib(r.textbooks);
      setLoading(false);
    })();
  }, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase(); if (!s) return lib;
    return lib.filter(t => [t.title, t.author, t.subject, t.grade_level].filter(Boolean).some(f => f.toLowerCase().includes(s)));
  }, [lib, q]);
  const handleAdd = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    const r = await api('/teacher/syllabus/library/textbooks', { method: 'POST', body: JSON.stringify(form) });
    if (r.textbook) {
      toast.success('Added to your library');
      setLib(prev => [r.textbook, ...prev]);
      setShowAdd(false);
      setForm({ title: '', author: '', subject: '', grade_level: '', description: '' });
    } else toast.error(r.detail || 'Failed');
  };
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="syll-textbook-picker">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-zinc-100 bg-gradient-to-br from-indigo-50 via-white to-[#34A853]/6 flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-indigo-700 mb-0.5">Textbook library</p>
            <h3 className="text-lg font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Pick a textbook to ground the AI</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-3 border-b border-zinc-100 flex items-center gap-2">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by title, author, subject…" className="flex-1 h-9 px-3 rounded-lg border border-zinc-200 text-[12px] focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20" data-testid="syll-textbook-search" />
          <button onClick={() => setShowAdd(s => !s)} className="text-[11px] font-bold text-[#0d9488] hover:bg-[#34A853]/10 border border-[#34A853]/30 px-3 py-2 rounded-lg flex items-center gap-1" data-testid="syll-textbook-add-toggle"><Plus className="w-3 h-3" /> New</button>
        </div>
        {showAdd && (
          <div className="px-6 py-3 border-b border-zinc-100 bg-gray-50 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Title *" className="inp2" data-testid="syll-new-tb-title" />
              <input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} placeholder="Author" className="inp2" />
              <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Subject" className="inp2" />
              <input value={form.grade_level} onChange={e => setForm(f => ({ ...f, grade_level: e.target.value }))} placeholder="Grade / Level" className="inp2" />
            </div>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Short description / scope (helps the AI)" className="w-full text-[11px] rounded-md border border-zinc-200 p-2 resize-none focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="text-[11px] font-semibold text-gray-500 hover:text-gray-800 px-3 py-1.5 rounded-md">Cancel</button>
              <button onClick={handleAdd} disabled={!form.title.trim()} className="text-[11px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] disabled:opacity-40 px-3 py-1.5 rounded-md" data-testid="syll-new-tb-save">Save to library</button>
            </div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {loading && <p className="text-[12px] text-gray-400 text-center py-6">Loading library…</p>}
          {!loading && filtered.length === 0 && <p className="text-[12px] text-gray-400 text-center py-6">No textbooks match.</p>}
          {filtered.map(tb => {
            const already = existingTitles.has(tb.title.toLowerCase());
            return (
              <button
                key={tb.id}
                disabled={already}
                onClick={() => onPick(tb)}
                className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition-all ${already ? 'border-zinc-100 bg-gray-50 opacity-60 cursor-not-allowed' : 'border-zinc-200 hover:border-[#34A853]/40 hover:bg-[#34A853]/5'}`}
                data-testid={`syll-tb-pick-${tb.id}`}
              >
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-50 to-[#34A853]/10 border border-zinc-200 flex items-center justify-center"><BookMarked className="w-4 h-4 text-indigo-600" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{tb.title}</p>
                  <p className="text-[11px] text-gray-500 truncate">{[tb.author, tb.subject, tb.grade_level].filter(Boolean).join(' · ')}</p>
                </div>
                <span className="text-[10px] font-bold text-[#0d9488]">{already ? 'Added' : 'Pick →'}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Standards framework picker ──────────────────────────────────────────────
function StandardsPicker({ currentStandards, onClose, onPick }) {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/teacher/syllabus/standards/frameworks');
        if (!cancelled && r?.frameworks) setFrameworks(r.frameworks);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="syll-standards-picker">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-zinc-100 bg-gradient-to-br from-[#34A853]/8 via-white to-indigo-50 flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#0d9488] mb-0.5 flex items-center gap-1"><Globe className="w-3 h-3" /> Align to world standards</p>
            <h3 className="text-lg font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Pick a curriculum framework</h3>
            <p className="text-[12px] text-gray-500 mt-1">We'll append the framework to your Standards field and use it to ground the AI.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-2">
          {loading && <p className="text-[12px] text-gray-400 text-center col-span-full py-6">Loading frameworks…</p>}
          {frameworks.map(f => {
            const already = currentStandards.toLowerCase().includes(f.label.toLowerCase());
            return (
              <button
                key={f.id}
                onClick={() => onPick(f)}
                className={`text-left rounded-xl border p-3 flex items-start gap-3 transition-all ${already ? 'border-emerald-200 bg-emerald-50/60' : 'border-zinc-200 hover:border-[#34A853]/40 hover:bg-[#34A853]/5'}`}
                data-testid={`syll-framework-${f.id}`}
              >
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#34A853]/10 to-indigo-50 border border-zinc-200 flex items-center justify-center flex-shrink-0"><Globe className="w-4 h-4 text-[#0d9488]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-[13px] font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{f.label}</p>
                    {already && <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Added</span>}
                  </div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{f.grades}</p>
                  <p className="text-[11px] text-gray-600 mt-1 leading-relaxed">{f.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Generate-full dialog ────────────────────────────────────────────────────
function GenerateDialog({ onClose, onSubmit, hasContext }) {
  const [frameworks, setFrameworks] = useState([]);
  const [frameworkId, setFrameworkId] = useState('');
  const [extra, setExtra] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('/teacher/syllabus/standards/frameworks');
        if (!cancelled && r?.frameworks) setFrameworks(r.frameworks);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="syll-generate-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-zinc-100 bg-gradient-to-br from-[#34A853]/8 via-white to-indigo-50 flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#0d9488] mb-0.5 flex items-center gap-1"><Wand2 className="w-3 h-3" /> One-shot generation</p>
            <h3 className="text-lg font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Generate a full world-standard syllabus</h3>
            <p className="text-[12px] text-gray-500 mt-1">AI will build objectives, standards, 5–8 units (with topics, prerequisites, differentiation, rubric-based assessments) in one shot.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Align to framework (optional)</p>
            <div className="grid grid-cols-2 gap-1.5">
              <button onClick={() => setFrameworkId('')} className={`text-[11px] font-semibold px-3 py-2 rounded-md border ${frameworkId === '' ? 'bg-[#34A853]/10 border-[#34A853]/40 text-[#0d9488]' : 'bg-white border-zinc-200 text-gray-600 hover:border-zinc-300'}`}>Auto-detect</button>
              {frameworks.map(f => (
                <button
                  key={f.id}
                  onClick={() => setFrameworkId(f.id)}
                  className={`text-[11px] font-semibold px-3 py-2 rounded-md border text-left ${frameworkId === f.id ? 'bg-[#34A853]/10 border-[#34A853]/40 text-[#0d9488]' : 'bg-white border-zinc-200 text-gray-600 hover:border-zinc-300'}`}
                  data-testid={`syll-gen-framework-${f.id}`}
                >{f.label}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Extra instructions (optional)</p>
            <textarea
              value={extra} onChange={e => setExtra(e.target.value)} rows={3}
              placeholder="e.g. Emphasise lab inquiry; include one interdisciplinary project; assessments should be 60% formative / 40% summative."
              className="w-full text-[12px] rounded-lg border border-zinc-200 p-3 resize-none focus:outline-none focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
              data-testid="syll-gen-extra"
            />
          </div>
          <p className="text-[11px] text-gray-500">{hasContext ? 'Attached PDFs & textbooks will be used as grounding.' : 'Tip: attach a PDF or pick a textbook for grounded output.'}</p>
        </div>
        <div className="px-6 py-4 border-t border-zinc-100 bg-gray-50/60 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[12px] font-semibold text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100">Cancel</button>
          <button
            onClick={() => onSubmit({ frameworkId, extraInstructions: extra })}
            className="text-[12px] font-bold text-white bg-gradient-to-r from-[#34A853] to-[#0d9488] hover:shadow-md px-5 py-2 rounded-lg flex items-center gap-1.5"
            data-testid="syll-gen-submit"
          >
            <Wand2 className="w-3.5 h-3.5" /> Generate syllabus
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Printable HTML view (opens in new tab) ──────────────────────────────────
function escapeHtml(s) { return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
function renderPrintableHtml(s) {
  const esc = escapeHtml;
  const units = s.units || [];
  const objectives = s.objectives || [];
  const tbs = s.textbooks || [];
  const pdfs = s.attached_pdfs || [];
  const unitsHtml = units.map((u, i) => {
    const topics = (u.topics || []).map(t => `
      <div class="topic">
        <p class="t-title">${esc(t.title)}${t.duration ? ` · <span class="t-dur">${esc(t.duration)}</span>` : ''}</p>
        ${(t.learning_objectives || []).length ? `<ul class="t-lo">${t.learning_objectives.map(o => `<li>${esc(o)}</li>`).join('')}</ul>` : ''}
        ${(t.subtopics || []).length ? `<p class="chips">${t.subtopics.map(st => `<span>${esc(st)}</span>`).join('')}</p>` : ''}
        ${(t.teaching_methods || []).length ? `<p class="t-methods"><strong>Methods:</strong> ${t.teaching_methods.map(esc).join(' · ')}</p>` : ''}
        ${(t.resources || []).length ? `<p class="t-res"><strong>Resources:</strong> ${t.resources.map(esc).join(' · ')}</p>` : ''}
      </div>
    `).join('');
    const a = u.assessment || {};
    return `
      <section class="unit">
        <h2><span class="unit-num">Unit ${u.unit_number || i + 1}</span> ${esc(u.title)}</h2>
        ${u.duration ? `<p class="meta">Duration: ${esc(u.duration)}</p>` : ''}
        ${u.description ? `<p class="desc">${esc(u.description)}</p>` : ''}
        ${(u.prerequisites || []).length ? `<p class="block"><strong>Prerequisites:</strong> ${u.prerequisites.map(esc).join(' · ')}</p>` : ''}
        ${u.differentiation ? `<p class="block"><strong>Differentiation:</strong> ${esc(u.differentiation)}</p>` : ''}
        ${topics ? `<h3>Topics</h3>${topics}` : ''}
        ${a.title || a.description ? `
          <div class="assess">
            <h3>Assessment${a.type ? ` — ${esc(a.type)}` : ''}${a.weight ? ` (${esc(a.weight)}%)` : ''}</h3>
            ${a.title ? `<p class="a-title">${esc(a.title)}</p>` : ''}
            ${a.description ? `<p class="a-desc">${esc(a.description)}</p>` : ''}
            ${(a.rubric_criteria || []).length ? `<p class="chips">${a.rubric_criteria.map(c => `<span>${esc(c)}</span>`).join('')}</p>` : ''}
          </div>
        ` : ''}
      </section>
    `;
  }).join('');
  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(s.title || 'Syllabus')} — Desky</title>
<style>
  @page { size: A4; margin: 18mm; }
  body { font-family: Manrope, Inter, system-ui, sans-serif; color: #111827; line-height: 1.5; max-width: 800px; margin: 0 auto; padding: 24px; }
  .cover { border-bottom: 4px solid #34A853; padding-bottom: 16px; margin-bottom: 24px; }
  .cover .eyebrow { color: #0d9488; font-weight: 700; text-transform: uppercase; letter-spacing: .14em; font-size: 11px; margin: 0 0 4px; }
  .cover h1 { margin: 0; font-size: 28px; font-weight: 800; letter-spacing: -0.01em; }
  .cover .meta { color: #6b7280; font-size: 13px; margin-top: 6px; }
  h2 { font-size: 18px; margin: 24px 0 8px; letter-spacing: -0.01em; }
  h3 { font-size: 14px; color: #0d9488; margin: 12px 0 6px; text-transform: uppercase; letter-spacing: .08em; }
  .block { margin: 6px 0; font-size: 13px; color: #374151; }
  p.desc { color: #4b5563; font-size: 13px; margin: 6px 0 10px; }
  p.meta { color: #6b7280; font-size: 11px; text-transform: uppercase; letter-spacing: .08em; margin: 0 0 8px; }
  .unit { break-inside: avoid; border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px 18px; margin-bottom: 14px; }
  .unit h2 { margin-top: 0; display: flex; align-items: baseline; gap: 10px; }
  .unit-num { background: #34A85310; color: #0d9488; font-size: 10px; font-weight: 800; padding: 3px 8px; border-radius: 4px; letter-spacing: .12em; text-transform: uppercase; }
  .topic { padding: 8px 0; border-top: 1px dashed #e5e7eb; }
  .topic:first-of-type { border-top: none; }
  .t-title { font-weight: 700; font-size: 13px; margin: 0 0 4px; }
  .t-dur { color: #6b7280; font-weight: 500; }
  .t-lo { margin: 4px 0; padding-left: 18px; font-size: 12px; color: #374151; }
  .t-methods, .t-res { font-size: 11px; color: #4b5563; margin: 3px 0; }
  .chips span { display: inline-block; background: #f3f4f6; border: 1px solid #e5e7eb; color: #374151; font-size: 10px; padding: 2px 7px; border-radius: 999px; margin: 2px 4px 2px 0; }
  .assess { margin-top: 10px; background: #fafafa; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; }
  .a-title { font-weight: 700; margin: 4px 0 2px; font-size: 13px; }
  .a-desc { font-size: 12px; color: #374151; margin: 2px 0; }
  ul.obj { padding-left: 20px; margin: 6px 0; font-size: 13px; }
  .res-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12px; }
  .res-grid div { background: #fafafa; border: 1px solid #e5e7eb; padding: 8px 10px; border-radius: 8px; }
  .res-grid b { font-size: 10px; color: #0d9488; text-transform: uppercase; letter-spacing: .1em; display: block; margin-bottom: 2px; }
  .foot { margin-top: 28px; text-align: center; color: #9ca3af; font-size: 10px; border-top: 1px solid #e5e7eb; padding-top: 10px; }
  @media print { body { padding: 0; } .noprint { display: none; } }
  .noprint { text-align: right; margin-bottom: 12px; }
  .noprint button { background: #34A853; color: white; border: 0; padding: 8px 16px; border-radius: 8px; font-weight: 700; cursor: pointer; }
</style></head>
<body>
  <div class="noprint"><button onclick="window.print()">Print / Save as PDF</button></div>
  <div class="cover">
    <p class="eyebrow">Syllabus · Desky</p>
    <h1>${esc(s.title)}</h1>
    <p class="meta">${[s.subject, s.grade_level, s.duration].filter(Boolean).map(esc).join(' · ')}</p>
    ${s.standards ? `<p class="meta" style="margin-top:10px; color:#0d9488; text-transform:none; letter-spacing:0; font-weight:600;">Aligned to: ${esc(s.standards).replace(/\n/g, ' · ')}</p>` : ''}
  </div>
  ${objectives.length ? `<section><h3>Learning objectives</h3><ul class="obj">${objectives.map(o => `<li>${esc(o)}</li>`).join('')}</ul></section>` : ''}
  ${(tbs.length || pdfs.length) ? `<section><h3>Grounding material</h3><div class="res-grid">${tbs.map(t => `<div><b>Textbook</b>${esc(t.title)}${t.author ? ` — ${esc(t.author)}` : ''}</div>`).join('')}${pdfs.map(p => `<div><b>PDF</b>${esc(p.title)}</div>`).join('')}</div></section>` : ''}
  ${unitsHtml ? `<section><h3 style="margin-top:28px;">Units</h3>${unitsHtml}</section>` : ''}
  <div class="foot">Built with Desky AI Syllabus Architect · ${new Date().toLocaleDateString()}</div>
</body></html>`;
}

